'use client';

import { useState, useEffect, useCallback } from 'react';
import { useT } from '@/components/i18n-provider';
import { requestRemixFor } from '@/components/workspace/remix-intent';
import { AppWindow, ChevronRight, Grid2x2Plus, MessageCircle, GitFork } from 'lucide-react';
import { Avatar } from '@/components/avatar';
import { ChannelFeed } from './channel-feed';
import { AppStoreHeader } from './app-store-header';
import { WidgetMark } from '@/components/workspace/panels/content-preview';
import { ManageApp } from './manage-app';
import { useAuth } from '@/components/auth-provider';
import { AttachEntityPicker, type AttachedEntity } from '@/components/create/attach-entity-picker';
import { PostDetailView } from '@/components/posts/post-detail-view';
import { usePanelBackStack } from '@/components/workspace/panels/use-panel-back';
import { useWorkspaceCardActions } from '@/components/workspace/card-actions-context';
import { appMarkSeed, widgetIconRef } from '@/lib/content/app-identity';
import { coverGradient } from '@/lib/marketplace/cover';

/**
 * A channel rendered INLINE in the rail panel (no iframe): header + subchannels
 * + post feed. Replaces the `/c/<slug>?embed=panel` iframe — instant, shares the
 * app's state, and shows the full structure (a `//apps/games` channel lists its
 * app subchannels; `//apps` lists its categories). `onOpen` drills into a
 * subchannel, so Back (usePanelBack) pops one level at a time.
 */
/** The listing's own marketplace row — the app-store hero's data. Shape is
 *  the /api/marketplace/channels item (same payload CatalogCard renders). */
type ListingRow = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  logoUrl: string | null;
  bannerUrl: string | null;
  color: string | null;
  iconEmoji?: string | null;
  verificationStatus: string | null;
  primaryContentId: string | null;
  primaryAgentId: string | null;
  widgetMetadata: Record<string, unknown> | null;
  widgetPostId: string | null;
  makerUsername: string | null;
  agentUsername: string | null;
  agentDisplayName: string | null;
  agentAvatarUrl: string | null;
};

type Sub = {
  // Category rows carry no id/logoUrl (only slug/name/color/iconEmoji); listing
  // rows carry the full set. Key by slug, which both always have.
  id?: string;
  name: string;
  slug: string;
  description?: string | null;
  logoUrl?: string | null;
  iconEmoji?: string | null;
};

export function ChannelPanelView({
  slug,
  name,
  canPost,
  onOpen,
  onDeleted,
}: {
  slug: string;
  name: string;
  canPost: boolean;
  onOpen: (slug: string, name: string) => void;
  /** Called after the owner deletes this channel (host pops the drill). */
  onDeleted?: () => void;
}) {
  const t = useT();
  const [subs, setSubs] = useState<Sub[]>([]);
  const [listing, setListing] = useState<ListingRow | null>(null);
  const { user } = useAuth();
  const cardActions = useWorkspaceCardActions();
  // In-panel post drill-down (same LIFO pattern as the workspace Feed panel):
  // a post click or a card's "Edit" swaps the panel body for PostDetailView
  // instead of routing to /p/[id] — which, from inside the panel overlay,
  // navigated the whole page out from under it. `autoEditId` opens the detail
  // view already in its inline edit form.
  const [postStack, setPostStack] = useState<string[]>([]);
  const [autoEditId, setAutoEditId] = useState<string | null>(null);
  const pushPost = useCallback((id: string) => {
    setPostStack((s) => (s[s.length - 1] === id ? s : [...s, id]));
    setAutoEditId(null);
  }, []);
  const popPost = useCallback(() => {
    setPostStack((s) => s.slice(0, -1));
    setAutoEditId(null);
  }, []);
  const editPost = useCallback((id: string) => {
    setPostStack((s) => (s[s.length - 1] === id ? s : [...s, id]));
    setAutoEditId(id);
  }, []);
  const currentPostId = postStack[postStack.length - 1];
  usePanelBackStack(postStack.length, popPost);
  // Owner-only attach control (community channels): binds an app/agent to
  // the channel so its page carries the entity header + Open/Chat CTA.
  // Marketplace listings (apps/%, agents/%) are pipeline-managed.
  const isMarketplaceTree = /^(apps|agents)(\/|$)/.test(slug);
  const [isOwner, setIsOwner] = useState(false);
  const [attached, setAttached] = useState<AttachedEntity | null>(null);

  useEffect(() => {
    if (isMarketplaceTree || !user?.id) return;
    const ctrl = new AbortController();
    fetch(`/api/channels/${slug}`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d?.channel || ctrl.signal.aborted) return;
        const ch = d.channel as {
          ownerId?: string | null;
          createdById?: string | null;
          primaryContentId?: string | null;
          primaryAgentId?: string | null;
        };
        setIsOwner(ch.ownerId === user.id || ch.createdById === user.id);
        setAttached(
          ch.primaryContentId
            ? { kind: 'app', id: ch.primaryContentId, name: 'Attached app' }
            : ch.primaryAgentId
              ? { kind: 'agent', id: ch.primaryAgentId, name: 'Attached agent' }
              : null,
        );
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, [slug, user?.id, isMarketplaceTree]);

  const saveAttachment = async (next: AttachedEntity | null) => {
    const prev = attached;
    setAttached(next); // optimistic
    try {
      const res = await fetch(`/api/channels/${slug}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          primary_content_id: next?.kind === 'app' ? next.id : null,
          primary_agent_id: next?.kind === 'agent' ? next.id : null,
        }),
      });
      if (!res.ok) setAttached(prev); // revert silently; picker stays usable
    } catch {
      setAttached(prev);
    }
  };

  useEffect(() => {
    const ctrl = new AbortController();
    setSubs([]);
    setListing(null);
    const parts = slug.split('/');
    const root = parts[0];
    // The `//apps` / `//agents` marketplace roots + their category channels have
    // structured children (categories → app/agent listings). Regular channels
    // have none — just a feed. A LISTING slug is its PARENT's item: fetch the
    // parent level first and look for ourselves among its items — found means
    // this level is an app/agent page (render the app-store hero, no children),
    // not found means it is a category (render its children as before).
    if (root === 'apps' || root === 'agents') {
      const parentCategory = parts.slice(1, -1).join('/');
      const selfQ = new URLSearchParams({ root });
      if (parentCategory) selfQ.set('category', parentCategory);
      fetch(`/api/marketplace/channels?${selfQ.toString()}`, { signal: ctrl.signal })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!d || ctrl.signal.aborted) return;
          const self = parts.length > 1
            ? ((d.items ?? []) as ListingRow[]).find((i) => i.slug === slug) ?? null
            : null;
          if (self) {
            setListing(self);
            return;
          }
          const category = parts.slice(1).join('/');
          if (!category) {
            // At the root the children are the category channels.
            setSubs(((d.categories ?? []) as Sub[]));
            return;
          }
          // We are a CATEGORY: our children are the listings under us.
          const q = new URLSearchParams({ root, category });
          fetch(`/api/marketplace/channels?${q.toString()}`, { signal: ctrl.signal })
            .then((r) => (r.ok ? r.json() : null))
            .then((dd) => {
              if (!dd || ctrl.signal.aborted) return;
              setSubs(((dd.items ?? []) as Sub[]));
            })
            .catch(() => {});
        })
        .catch(() => {});
    }
    return () => ctrl.abort();
  }, [slug]);

  // Post drill-down replaces the channel body; Back (usePanelBackStack) pops.
  if (currentPostId) {
    return (
      <div className="flex flex-col px-1 pb-4">
        <PostDetailView
          key={currentPostId}
          postId={currentPostId}
          embedded
          onBack={popPost}
          onOpenPost={pushPost}
          autoEdit={currentPostId === autoEditId}
        />
      </div>
    );
  }

  const parts = slug.split('/');
  const root = parts[0] === 'agents' ? 'agents' : 'apps';
  const isAgentListing = !!listing?.primaryAgentId;
  const listingSeed = listing ? appMarkSeed(listing.widgetMetadata, listing.id) : '';
  const listingCover = listing ? coverGradient(listingSeed, listing.color).css : '';
  const listingIconRef = listing ? widgetIconRef(listing.widgetMetadata) : null;
  const isStaff = user?.role === 'admin' || user?.role === 'moderator';
  // Client-side gate mirrors the server page's canModerateChannel scope we can
  // resolve here: the maker (their username is in the listing payload) and
  // staff. Presentation only — ManageApp's writes re-check server-side.
  const canManage =
    !!listing && !!user && (isStaff || (!!listing.makerUsername && listing.makerUsername === user.username));

  const spawnListing = async () => {
    if (!listing || !cardActions) return;
    if (isAgentListing && listing.primaryAgentId && listing.agentUsername) {
      await cardActions.addAgent({
        agentId: listing.primaryAgentId,
        username: listing.agentUsername,
        displayName: listing.agentDisplayName ?? listing.agentUsername,
        avatarUrl: listing.agentAvatarUrl ?? null,
      });
    } else if (listing.primaryContentId) {
      await cardActions.addWidget({
        contentId: listing.primaryContentId,
        title: listing.name,
        metadata: listing.widgetMetadata,
        sourceWidgetPostId: listing.widgetPostId,
      });
    }
  };
  // Remix from the listing: open the app on the canvas and ask its node to
  // open the Remix dialog (the same one the window header offers), so the
  // store page is no longer the one surface without the affordance.
  const remixListing = async () => {
    if (!listing || !cardActions || !listing.primaryContentId) return;
    const created = await cardActions.addWidget({
      contentId: listing.primaryContentId,
      title: listing.name,
      metadata: listing.widgetMetadata,
      sourceWidgetPostId: listing.widgetPostId,
    });
    if (created) requestRemixFor(created.id);
  };

  return (
    <div className="flex flex-col px-1 pb-4">
      {listing ? (
        // App/agent LISTING: the full app-store hero — cover, identity,
        // description, Open/Chat, and Manage for the maker/staff — the same
        // anatomy as the public /c page, feeding the same posts underneath.
        <div className="pb-3 pt-1">
          <AppStoreHeader
            coverCss={listingCover}
            bannerUrl={listing.bannerUrl}
            logoUrl={listing.logoUrl}
            iconEmoji={listing.iconEmoji}
            fallbackIcon={<AppWindow className="h-7 w-7" strokeWidth={1.6} />}
            icon={
              isAgentListing && listing.agentUsername ? (
                <Avatar
                  seed={listing.agentUsername}
                  type="agent"
                  src={listing.agentAvatarUrl}
                  className="h-16 w-16 border-2 border-background bg-white shadow-sm"
                />
              ) : listingIconRef ? (
                // The app's OWN declared mark. Without this the header read
                // the channel ROW only (no logo → a generic app-window glyph on a
                // uuid-hashed gradient), so an app looked like a different app
                // here than in the taskbar and on its card.
                <WidgetMark
                  iconRef={listingIconRef}
                  seed={listingSeed}
                  name={listing.name}
                  className="h-16 w-16 rounded-2xl border-2 border-background text-white"
                  style={{ background: listingCover }}
                />
              ) : undefined
            }
            name={listing.name}
            verified={listing.verificationStatus === 'verified'}
            pathLabel={`//${root}${parts.length > 2 ? '/' + parts.slice(1, -1).join('/') : ''}`}
            creator={
              listing.makerUsername ? { username: listing.makerUsername, avatarUrl: null } : null
            }
            description={listing.description}
          >
            <button
              type="button"
              onClick={() => void spawnListing()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-4 py-2 text-sm font-medium text-background transition-opacity hover:opacity-90"
            >
              {isAgentListing ? <MessageCircle className="h-4 w-4" /> : <Grid2x2Plus className="h-4 w-4" />}
              {isAgentListing ? t('workspace.canvas.node.chat') : t('posts.open')}
            </button>
            {!isAgentListing && !!listing.primaryContentId && !!user && (
              <button
                type="button"
                onClick={() => void remixListing()}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border/60 px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
              >
                <GitFork className="h-4 w-4" />
                {t('widget.remix')}
              </button>
            )}
            {canManage && (
              <ManageApp
                channelId={listing.id}
                slug={listing.slug}
                name={listing.name}
                description={listing.description ?? ''}
                color={listing.color}
                bannerUrl={listing.bannerUrl}
                logoUrl={listing.logoUrl}
                root={root}
                primaryContentId={listing.primaryContentId}
                primaryAgentId={listing.primaryAgentId}
              />
            )}
          </AppStoreHeader>
        </div>
      ) : (
      <div className="flex items-center gap-3 px-1 pb-3 pt-1">
        <Avatar seed={slug} type="channel" className="h-11 w-11 rounded-xl" />
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold">{name}</h2>
          <p className="text-xs text-muted-foreground">//{slug}</p>
        </div>
      </div>
      )}

      {isOwner && !isMarketplaceTree && (
        <div className="mb-3 px-1">
          <AttachEntityPicker value={attached} onChange={(v) => void saveAttachment(v)} />
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm(t('marketplace.confirmDeleteChannel'))) return;
              const res = await fetch(`/api/channels/${slug}`, { method: 'DELETE' });
              if (res.ok) onDeleted?.();
            }}
            className="mt-1.5 text-xs font-medium text-red-500/80 transition-colors hover:text-red-500"
          >
            {t('marketplace.deleteChannel')}
          </button>
        </div>
      )}

      {subs.length > 0 && (
        <div className="mb-3 overflow-hidden rounded-xl border border-border/60">
          {subs.map((s) => (
            <button
              key={s.slug}
              type="button"
              onClick={() => onOpen(s.slug, s.name)}
              className="flex w-full items-center gap-3 border-b border-border/40 px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-accent/40"
            >
              <Avatar seed={s.slug} type="channel" src={s.logoUrl ?? undefined} className="h-8 w-8" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{s.name}</div>
                <div className="truncate text-xs text-muted-foreground">//{s.slug}</div>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          ))}
        </div>
      )}

      <ChannelFeed channelSlug={slug} canPost={canPost} onOpenPost={pushPost} onEdit={editPost} />
    </div>
  );
}
